import { Component, EventEmitter, Input, OnInit, Output, OnDestroy, OnChanges, SimpleChanges } from '@angular/core';
import { FormBuilder, FormGroup, Validators, AbstractControl } from '@angular/forms';
import { DialogConfig, FormField } from './form-field.interface';
import { Subscription } from 'rxjs';
import { EditorComponent } from '@tinymce/tinymce-angular';
import { init } from '../../../pages/editorconfig';


@Component({
  selector: 'app-reusable-dialog',
  standalone: false,
  templateUrl: './reusable-dialog.component.html',
  styleUrls: ['./reusable-dialog.component.scss']
})
export class ReusableDialogComponent implements OnInit, OnDestroy, OnChanges {
  @Input() visible: boolean = false;
  @Input() config!: DialogConfig;
  @Input() initialData: any = {};
  @Input() viewMode: boolean = false; 
  
  @Output() visibleChange = new EventEmitter<boolean>();
  @Output() onSubmit = new EventEmitter<any>();
  @Output() onCancel = new EventEmitter<void>();
  @Output() onClose = new EventEmitter<void>();

  init: EditorComponent['init'] = init;

  form: FormGroup;
  private subscriptions = new Subscription();

  constructor(private fb: FormBuilder) {
    this.form = this.fb.group({});
  }

  ngOnInit(): void {
    this.buildForm();
    this.populateForm();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['initialData'] && !changes['initialData'].firstChange) {
      console.log('Reusable Dialog - initialData changed:', this.initialData);
      this.populateForm();
    }
    if (changes['viewMode'] && !changes['viewMode'].firstChange) {
      if (this.viewMode) {
        this.form.disable();
      } else {
        this.form.enable();
      }
    }
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  private buildForm(): void {
    const formControls: { [key: string]: AbstractControl } = {};
    
    this.config.fields.forEach(field => {
      const validators = [];
      
      if (field.validation?.required) {
        validators.push(Validators.required);
      }
      
      if (field.validation?.pattern) {
        validators.push(Validators.pattern(field.validation.pattern));
      }
      
      if (field.validation?.minLength) {
        validators.push(Validators.minLength(field.validation.minLength));
      }
      
      if (field.validation?.maxLength) {
        validators.push(Validators.maxLength(field.validation.maxLength));
      }
      
      if (field.validation?.custom) {
        validators.push(this.customValidator(field.validation.custom));
      }

      formControls[field.key] = this.fb.control(
        { value: '', disabled: field.disabled || false }, 
        validators
      );
    });

    this.form = this.fb.group(formControls);
  }

  private customValidator(validatorFn: (value: any) => string | null) {
    return (control: AbstractControl) => {
      const result = validatorFn(control.value);
      return result ? { custom: { message: result } } : null;
    };
  }

  private populateForm(): void {
    if (this.initialData && Object.keys(this.initialData).length > 0 && this.form) {
      console.log('Reusable Dialog - Populating form with:', this.initialData);
      this.form.patchValue(this.initialData);
      
      if (this.viewMode) {
        this.form.disable();
      } else {
        this.form.enable();
      }
    } else {
      console.log('Reusable Dialog - No data to populate or form not ready, resetting form');
      this.form.reset();
    }
  }

  onSubmitForm(): void {
    if (this.form.valid) {
      this.onSubmit.emit(this.form.value);
    } else {
      this.markFormGroupTouched();
    }
  }

  onCancelForm(): void {
    this.onCancel.emit();
    this.closeDialog();
  }

  closeDialog(): void {
    this.visible = false;
    this.visibleChange.emit(false);
    this.onClose.emit();
    this.form.reset();
    this.initialData = {}; // Reset initial data
  }

  private markFormGroupTouched(): void {
    Object.keys(this.form.controls).forEach(key => {
      const control = this.form.get(key);
      control?.markAsTouched();
    });
  }

  getFieldError(fieldKey: string): string {
    const control = this.form.get(fieldKey);
    if (control?.touched && control?.errors) {
      if (control.errors['required']) {
        return `${this.getFieldLabel(fieldKey)} is required.`;
      }
      if (control.errors['pattern']) {
        const field = this.config.fields.find(f => f.key === fieldKey);
        return field?.patternErrorMessage || `${this.getFieldLabel(fieldKey)} format is invalid.`;
      }
      if (control.errors['minlength']) {
        return `${this.getFieldLabel(fieldKey)} must be at least ${control.errors['minlength'].requiredLength} characters.`;
      }
      if (control.errors['maxlength']) {
        return `${this.getFieldLabel(fieldKey)} must not exceed ${control.errors['maxlength'].requiredLength} characters.`;
      }
      if (control.errors['custom']) {
        return control.errors['custom'].message;
      }
    }
    return '';
  }

  private getFieldLabel(fieldKey: string): string {
    const field = this.config.fields.find(f => f.key === fieldKey);
    return field?.label || fieldKey;
  }

  isFieldRequired(fieldKey: string): boolean {
    const field = this.config.fields.find(f => f.key === fieldKey);
    return field?.validation?.required || false;
  }

  getFieldConfig(fieldKey: string): FormField | undefined {
    return this.config.fields.find(f => f.key === fieldKey);
  }
}
